import React, { useState } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import * as actions from '../../Redux/Actions/index';
import { showErrorToastMessage, showSuccessToastMessage } from '../Toasts/Toasts';
import ReassignmentModal from './ReassignmentModal';

interface IManagerProps {
  show: boolean;
  onHide: () => void;
  title: string;
  data: { _id: string; name: string }[];
  type: 'DEPARTMENT' | 'SUB_DEPARTMENT';
  refreshData: () => void;
  onReassignSuccess?: (oldId: string, newTarget: any) => void;
}

const DepartmentManagerModal = ({ show, onHide, title, data, type, refreshData, onReassignSuccess }: IManagerProps) => {
  const dispatch = useDispatch();

  // State for Confirmation Modal
  const [itemToDelete, setItemToDelete] = useState<{ id: string, name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // State for Reassignment Modal
  const [reassignData, setReassignData] = useState<any>(null);

  // 1. User clicks Trash Icon -> Open Confirmation
  const initiateDelete = (id: string, name: string) => {
    setItemToDelete({ id, name });
  };

  const [blockerMsg, setBlockerMsg] = useState("");

  // 2. User clicks "Yes" in Confirmation
  const confirmDelete = () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    const action = type === 'DEPARTMENT'
      ? actions.deleteDepartmentAction
      : actions.deleteSubDepartmentAction;

    dispatch(action(itemToDelete.id, null, (res: any) => {
      console.log("DELETE RESPONSE:", res);
      setIsDeleting(false);

      // Success Case
      if (res?.success || res?.code === 200) {
        showSuccessToastMessage("Deleted successfully.");
        refreshData();
        setItemToDelete(null); // Close Confirm Modal
      }
      // 412: Employees Exist Case
      // Note: Check how your axios error is structured. Usually: res.error.response.data.http_code
      else if (res?.error?.response?.status === 412) {
        setItemToDelete(null); // Close Confirm Modal

        if (type === 'SUB_DEPARTMENT') {
          // EXTRACT COUNT FROM: res.error.response.data.meta_data
          const metaData = res.error.response.data.meta_data;
          const count = Array.isArray(metaData) ? metaData.length : 0;


          setReassignData({
            id: itemToDelete.id,
            name: itemToDelete.name,
            count: count
          });
        } else {
          const msg = res?.error?.response?.data?.error?.error_description || "Cannot delete: Employees assigned.";
          setBlockerMsg(msg);
        }
      }

      // 3. Generic Error
      else if (res?.error?.response?.status === 400) {
        setItemToDelete(null);

        const msg = res?.error?.response?.data?.error?.error_description || "Cannot delete this item.";
        setBlockerMsg(msg);
      }

      // 4. Generic Error -> Show Toast
      else {
        const errorMsg = res?.error?.response?.data?.error?.error_description || "Delete failed.";
        showErrorToastMessage(errorMsg);
        setItemToDelete(null);
      }
    }));
  };

  return (
    <>
      {/* Main Manager List */}
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage {title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {data && data.length > 0 ? (
            <Table hover responsive>
              <tbody>
                {data.map((item) => (
                  <tr key={item._id}>
                    <td className="align-middle">{item.name}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => initiateDelete(item._id, item.name)}
                      >
                        <BsTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-center text-muted">No items found.</p>
          )}
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal (Replaces Browser Alert) */}
      <Modal show={!!itemToDelete} onHide={() => setItemToDelete(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setItemToDelete(null)} disabled={isDeleting}>No</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? <Spinner size="sm" animation="border" /> : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reassignment Modal (Only opens on 412 Error) */}
      {reassignData && (
        <ReassignmentModal
          show={!!reassignData}
          onHide={() => setReassignData(null)}
          conflictedItem={reassignData}
          onSuccess={(target: any) => {
            refreshData();
            if (onReassignSuccess && reassignData) {
              onReassignSuccess(reassignData.id, target);
            }
            setReassignData(null);
          }}
        />
      )}
      <Modal show={!!blockerMsg} onHide={() => setBlockerMsg("")} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Cannot Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: "1.1rem", fontWeight: "500" }}>
            {blockerMsg}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBlockerMsg("")}>
            Okay
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DepartmentManagerModal;